import { useParams } from 'react-router-dom'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { newThemeObject } from '../../newTheme'
import { tokens } from '../../styles/tokens'
import { useSelector } from '../../redux/hooks/useSelector'
import { NavBarButton } from './NavBarButton'
import User from './User'
import { Skeleton } from '@mui/material'
import { DashboardRounded, HelpOutlineRounded, DriveFolderUploadRounded } from '@mui/icons-material'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { getClientIdFromUser, isClientUser } from '../../services/keycloak.service'
import { Pages } from '../../router/router'
import { useTranslation } from 'react-i18next'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import ListRoundedIcon from '@mui/icons-material/ListRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SmartDisplayRoundedIcon from '@mui/icons-material/SmartDisplayRounded';
import { Portfolio } from '../../redux/reducers/portfolio'
import { INavBarButton } from '../../interfaces/layout'
import Divider from '../common/Divider'
import LogoPortilio from '../../assets/logo-text3.svg'
import LogoPortilio2 from '../../assets/logo-text2.png'
import LogoApex from '../../assets/APEX-CAPITAL_2.svg'
import LogoImperium from '../../assets/IMPERIUM-FINANCE.png'
import { isApexUser } from '../../utils/apex'
import { isDistributorUser } from '../../utils/distributor'
import { APEX, IMPERIUM_FINANCE } from '../../constants/businessTerritory'
import { Territory } from '../../redux/reducers/territories'

function useButtons(isApexUser: boolean, isDistributor: boolean) {
  const { t } = useTranslation()
  const { clientId: clientIdParam } = useParams<{ clientId: string }>()
  // Route /market-overview clientId param nemá — klientovi ho vezmeme z tokenu,
  // jinak by mu níže early return sebral celé menu.
  const clientId = clientIdParam ?? (isClientUser() ? getClientIdFromUser() : undefined)
  const buttons: INavBarButton[] = []
  const portfButtons: INavBarButton[] = []
  const managerButtons: INavBarButton[] = []

  const client = useSelector(state => state.clients.client)
  const portfolios = client.portfolios
  const user = useSelector(state => state.auth.user)
  const isImperiumUser = user && user.businessTerritoryId === IMPERIUM_FINANCE
  const hasCeoRole = !!user?.roles?.includes('CEO')

  const etfInvestingButton: INavBarButton = {
    icon: <OpenInNewRoundedIcon/>,
    function: () => window.open('https://etfs.imperium-finance.cz/opportunities', '_blank', 'noopener,noreferrer'),
    text: t('ETF_INVESTING'),
    id: 'button.id-etf-investing'
  }

  const apexInvestorsButton: INavBarButton = {
    icon: <OpenInNewRoundedIcon/>,
    function: () => window.open('https://apexcapital.cz/investors', '_blank', 'noopener,noreferrer'),
    text: t('APEX_INVESTORS'),
    id: 'button.id-apex-investors'
  }

  const marketButton: INavBarButton = {
    icon: <SmartDisplayRoundedIcon/>,
    link: `${Pages.MarketOverview}`,
    text: t('MARKET_OVERVIEW'),
    id: 'button.id-market'
  }

  if (!isClientUser() && isDistributor) {
    managerButtons.push({
      icon: <PersonRoundedIcon/>,
      link: `${Pages.Home}`,
      text: t('CLIENTS'),
      id: 'button.id3'
    })
    managerButtons.push({
      icon: <CreditCardRoundedIcon/>,
      link: `${Pages.DistributorMgmtFee}`,
      text: 'Management fee',
      id: 'button.id-ops-mgmt-fee'
    })
  }

  if (!isClientUser() && !isDistributor) {
    if ( !isApexUser ) {
      managerButtons.push({
        icon: <MenuBookRoundedIcon/>,
        link: `${Pages.TraderDiary}`,
        text: t('TRADER_DIARY'),
        id: 'button.id1'
      })
    }
    managerButtons.push({
      icon: <CreditCardRoundedIcon/>,
      link: `${Pages.OrderList}`,
      text: isApexUser ? t('INVESTMENT_UNITS') : t('ORDERS_LIST'),
      id: 'button.id2'
    })
    managerButtons.push({
      icon: <DescriptionRoundedIcon/>,
      link: `${Pages.InstrumentList}`,
      text: t('INSTRUMENTS_LIST'),
      id: 'button.id3'
    })
    managerButtons.push({
      icon: <PersonRoundedIcon/>,
      link: `${Pages.Home}`,
      text: t('CLIENTS'),
      id: 'button.id3'
    })
    managerButtons.push({
      icon: <FolderRoundedIcon/>,
      link: `${Pages.Ops}`,
      text: isApexUser ? 'Fond' : 'Generátor smluv',
      id: 'button.id-ops'
    })
  }

  if (!isClientUser() && isImperiumUser) {
    managerButtons.push(etfInvestingButton)
  }

  if (!isClientUser() && isApexUser) {
    managerButtons.push(apexInvestorsButton)
  }

  // Přehled trhu hned pod „Pro investory" (staff); pro klienty je managerButtons
  // jinak prázdné, takže tady je položka zároveň vždy viditelná (i bez clientId).
  managerButtons.push(marketButton)

  if (!clientId) {
    return [buttons, portfButtons, managerButtons]
  }
  if (!isClientUser()) {
    buttons.push({
      icon: <DashboardRounded/>,
      link: portfolios?.length>1?`${Pages.ClientDetail}/${clientId}`:'',
      text: t('ASSET_OVERVIEW'),
      id: 'button.id7'
    })
    portfolios.map((portfolio: Portfolio, index: number) => {
      // agregát „Všechna portfolia" nemá portfolioId — z pole ho NEfiltrujeme,
      // indexy v URL jsou poziční (deep-linky), jen ho vizuálně odlišíme
      const isAggregate = !portfolio.portfolioId
      buttons.push({
        icon: isAggregate ? <GridViewRoundedIcon/> : <PieChartRoundedIcon/>,
        link: `${Pages.ClientDetail}/${clientId}/${index}`,
        // mimo detail klienta prijde agregat z API se syrovym nazvem "PT_ALL"
        // (prejmenovava ho az ClientDetail) - klient ho vidi napr. na Trznich prehledech
        text: isAggregate ? t('ALL_PORTFOLIOS') : (portfolio.name || (index + 1).toString()),
        id: 'button.id8',
        sub: true,
        aggregate: isAggregate
      })
    })
    buttons.push({
      icon: <PaymentsRoundedIcon/>,
      text: t('TRANSACTIONS'),
      id: 'button.id9'
    })
    buttons.push({
      icon: <SwapHorizRoundedIcon/>,
      link: `${Pages.ClientDetailDeal}/${clientId}`,
      text: t('DEAL_TRANSACTION'),
      id: 'button.id10'
    })
    buttons.push({
      icon: <AccountBalanceWalletRoundedIcon/>,
      link: `${Pages.ClientDetailCashflow}/${clientId}`,
      text: t('CASHFLOW_TRANSACTION'),
      id: 'button.id11'
    })
    buttons.push({
      icon: <ListRoundedIcon/>,
      text: t('TAX_REPORT'),
      id: 'button.id12'
    })
    buttons.push({
      icon: <ReceiptLongRoundedIcon/>,
      link: `${Pages.ClientDetailTaxPossition}/${clientId}`,
      text: t('TAX_REPORT_TRANSACTION'),
      id: 'button.id13'
    })
    buttons.push({
      icon: <SummarizeRoundedIcon/>,
      link: `${Pages.ClientDetailTaxSummary}/${clientId}`,
      text: t('TAX_REPORT_SUMMARY'),
      id: 'button.id14'
    })
    buttons.push({
      icon: <FolderRoundedIcon/>,
      link: `${Pages.ClientDetailDocuments}/${clientId}`,
      text: t('DOCUMENTS'),
      id: 'button.id-client-documents'
    })
    if (!isDistributor) {
      // Pokyny per klient jen pro Apex CEO — u Imperia by šlo o duplikát
      // obecné položky Pokyny nahoře v menu (proto to kdysi bot smazal).
      if (isApexUser && hasCeoRole) {
        buttons.push({
          icon: <CreditCardRoundedIcon/>,
          link: `${Pages.ClientOrderList}/${clientId}`,
          text: t('ORDERS'),
          id: 'button.id15'
        })
      }
      buttons.push({
        icon: <HelpOutlineRounded/>,
        link: `${Pages.InvestmentQuestionnaire}/${clientId}`,
        text: t('INVESTMENT_QUESTIONNAIRE'),
        id: 'button.id16'
      })
      buttons.push({
        icon: <DriveFolderUploadRounded/>,
        link: `${Pages.ImportDocuments}/${clientId}`,
        text: t('IMPORT_DOCUMENTS'),
        id: 'button.id17'
      })
    }
  } else {
    buttons.push({
      icon: <DashboardRounded/>,
      link: `${Pages.ClientDetail}/${clientId}`,
      text: t('ASSET_OVERVIEW'),
      id: 'button.id18'
    })
    portfolios.map((portfolio: Portfolio, index: number) => {
      const isAggregate = !portfolio.portfolioId
      buttons.push({
        icon: isAggregate ? <GridViewRoundedIcon/> : <PieChartRoundedIcon/>,
        link: `${Pages.ClientDetail}/${clientId}/${index}`,
        // mimo detail klienta prijde agregat z API se syrovym nazvem "PT_ALL"
        // (prejmenovava ho az ClientDetail) - klient ho vidi napr. na Trznich prehledech
        text: isAggregate ? t('ALL_PORTFOLIOS') : (portfolio.name || (index + 1).toString()),
        id: 'button.id8',
        sub: true,
        aggregate: isAggregate
      })
    })
    buttons.push({
      icon: <HelpOutlineRounded/>,
      link: `${Pages.InvestmentQuestionnaire}/${clientId}`,
      text: t('INVESTMENT_QUESTIONNAIRE'),
      id: 'button.id19'
    })
    buttons.push({
      icon: <DriveFolderUploadRounded/>,
      link: `${Pages.ImportDocuments}/${clientId}`,
      text: t('IMPORT_DOCUMENTS'),
      id: 'button.id20'
    })
    if (isImperiumUser) {
      buttons.push(etfInvestingButton)
    }
    if (isApexUser) {
      buttons.push(apexInvestorsButton)
    }
  }
  // HIDDEN: Pokyny page disabled for clients - shows errors and unnecessary transactions
  // if ( isClientUser() && isApexUser ) {
  //   buttons.push({
  //     icon: <CreditCardRoundedIcon/>,
  //     link: `${Pages.ClientOrderList}/${clientId}`,
  //     text: t('ORDERS_LIST'),
  //     id: 'button.id21'
  //   })
  // }


  return [buttons, portfButtons, managerButtons]
}

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    background: tokens.color.white,
    boxShadow: tokens.shadow.sm,
    justifyContent: 'left',
    alignItems: 'stretch',
    padding: theme.spacing(2),
    gap: '8px',
    borderRadius: newThemeObject.roundness.basic,
  },
  navScroll: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '8px',
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    '& > *': {
      flexShrink: 0,
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${tokens.color.neutral[200]} transparent`,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.color.neutral[200],
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
  },
  logoWrapper: {
    width: '100%',
    boxSizing: 'border-box',
    height: '85px',
    marginTop: '-16px',
    marginBottom: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: tokens.radius.lg,
  },
  logoPortilioWrapper: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginTop: 'auto',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '8px 16px',
    borderRadius: tokens.radius.lg,
  },
  logoPortilio: {
    height: '33px',
    width: 'auto',
  },
  logoPortilio2: {
    width: '104px',
    height: '23px',
  },
  logoApex: {
    height: '46px',
  },
  logoImperium: {
    height: '38px',
    width: '115px',
    mixBlendMode: 'multiply',
  },
  client: {
    fontWeight: '600',
    fontSize: '0.9rem',
    color: tokens.color.neutral[900],
    margin: 0,
    marginLeft: '10px',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    textWrap: 'wrap',
    maxWidth: '80%',
  },
  text: {
    fontWeight: '600',
    fontSize: 12,
    margin: 0,
  },
  iconContainer: {
    marginRight: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: '30px',
    color: 'inherit',
  },
  wannaBeButtonWrapper: {
    display: 'flex',
    width: '100%',
    boxSizing: 'border-box',
    justifyContent: 'left',
    padding: 10,
    paddingBottom: 0,
    borderRadius: newThemeObject.roundness.small,
    alignItems: 'center',
  },
  territorySwitcher: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    width: '100%',
    boxSizing: 'border-box',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  territoryLogo: {
    height: '28px',
    cursor: 'pointer',
    margin: '2px 4px',
    filter: 'grayscale(1)',
    opacity: 0.5,
    transition: `filter ${tokens.transition.fast}, opacity ${tokens.transition.fast}`,
    '&:hover': {
      filter: 'grayscale(0.3)',
      opacity: 0.85,
    },
  },
  territoryLogoSelected: {
    height: '28px',
    margin: '2px 4px',
  },
}))

interface Props {
  onTerritoryChange?: (territory: Territory) => void
}

export const NavBar: React.FC<Props> = ({ onTerritoryChange }) => {
  const classes = useStyles()
  const client = useSelector(state => state.clients.client)
  const user = useSelector(state => state.auth.user);
  const territories = useSelector(state => state.territories.territories)
  const { t } = useTranslation()
  const openedClient = useParams<{ clientId: string }>().clientId
  const [buttons, portfButtons, managerButtons] = useButtons(isApexUser(user), isDistributorUser(user))

  return (
    <div className={classes.wrapper}>
      <div className={classes.logoWrapper}>
      {user && user.businessTerritoryId === IMPERIUM_FINANCE &&
        <img src={LogoImperium} className={classes.logoImperium}/>
      }
      {user && user.businessTerritoryId === APEX &&
        <img src={LogoApex} className={classes.logoApex}/>
      }
      {!user || ( user.businessTerritoryId!==APEX && user.businessTerritoryId!==IMPERIUM_FINANCE) &&
        <img src={LogoPortilio2} className={classes.logoPortilio2}/>
      }
      </div>
      <div className={classes.navScroll}>
        {!!parseInt(openedClient) && !(client.firstName || client.surname) && (
          <Skeleton className={classes.client} variant="text" height={25} />
        )}
        {managerButtons && managerButtons.length !== 0 && (
          <>
            {managerButtons.map(button => (
              <NavBarButton button={button} />
            ))}
            {buttons && buttons.length !== 0 && <Divider size="sm" />}
          </>
        )}
        {buttons?.map(button => (
          <NavBarButton button={button} />
        ))}
      </div>

      {territories && territories.length > (isClientUser() ? 1 : 0) && (
        <div className={classes.territorySwitcher}>
          {territories.map((territory: Territory) => (
            <img
              src={'data:image/jpeg;base64,'+territory.logo}
              alt={territory.description}
              className={user && user.businessTerritoryId===territory.businessTerritoryId?classes.territoryLogoSelected:classes.territoryLogo}
              onClick={()=>{onTerritoryChange?.(territory)}}
            />
          ))}
        </div>
      )}

      <User />

            <div className={classes.logoPortilioWrapper}>
              <img src={LogoPortilio} alt="Multi tag" className={classes.logoPortilio}/>
            </div>

    </div>
  )
}
